import { Button } from "@/components/ui/buttons/button";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Pokemon } from "prisma-layer";
import { DuelState } from "types/duels/types";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import classNames from "classnames";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokeNames } from "types/pokemon/poke-names";
import { calculatePurity } from "@/utils/talent-utils";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { S3_BASE_URL } from "@/utils/s3";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import { loadAnimXML } from "@/utils/sprites/sheet-cut";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { MiniSkillCard } from "@/components/pokemon/mini-skill-card";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { RoundAnnouncement } from "./round-announcement";
import { isXsScreen } from "@/utils/screen-size-check";
import { DuelPokemon } from "types/duel-dynamodb";

export interface DuelScreenMiddleSectionProps {
  handleSubmitSelection: () => void;
  selectedPokemonIds: number[];
  picksCount: number;
  duelState: DuelState;
  teamNames: {
    blueTeamName: string;
    redTeamName: string;
    blueTeamCountry?: string | null;
    redTeamCountry?: string | null;
  };
  inspectedPokemon?: (DuelPokemon & { team: "A" | "B" | undefined }) | null;
  onCancelInspect?: () => void;
  stepType?: string | undefined;
  introStatus?: "idle" | "presentation" | "waiting" | "completed";
  isAutoSelecting?: boolean;
}

interface StepAction {
  type: "pick" | "ban";
  actor: "you" | "opponent";
  pokemonIds: number[];
}

interface StepResultState {
  actions: StepAction[];
}

export const DuelScreenMiddleSection = ({
  duelState,
  teamNames,
  inspectedPokemon,
  onCancelInspect,
  handleSubmitSelection,
  selectedPokemonIds,
  picksCount,
  stepType,
  introStatus,
  isAutoSelecting = false,
}: DuelScreenMiddleSectionProps) => {
  // State to track which round has been announced
  const [lastAcknowledgedRound, setLastAcknowledgedRound] = useState<
    number | null
  >(null);
  // State to force display of announcement component
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  // State for showing step results (picks/bans)
  const [activeStepResult, setActiveStepResult] =
    useState<StepResultState | null>(null);
  const [isStepResultExiting, setIsStepResultExiting] = useState(false);

  // Delay showing "Waiting for Opponent" to avoid flicker when the server responds quickly
  const [shouldShowWaiting, setShouldShowWaiting] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (duelState.status === "waiting_for_opponent_pick") {
      timer = setTimeout(() => {
        setShouldShowWaiting(true);
      }, 1000);
    } else {
      setShouldShowWaiting(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [duelState.status]);

  // Refs to track previous state for diffing
  const prevYourPicks = useRef<number[]>([]);
  const prevOpponentPicks = useRef<number[]>([]);
  const prevYourBans = useRef<number[]>([]);
  const prevOpponentBans = useRef<number[]>([]);
  // Track if this is the initial mount (to skip showing step results on reconnection)
  const isInitialMount = useRef(true);

  // Determine current round
  const currentRound =
    duelState.current_round_index ??
    (duelState.current_step?.round_index ||
      duelState.current_battle?.round_index ||
      (duelState.last_battle ? duelState.last_battle.round_index + 1 : 1));

  // Effect to trigger announcement when round changes
  useEffect(() => {
    // Only trigger if:
    // 1. We have a valid round
    // 2. It hasn't been announced yet
    // 3. Intro presentation is done (waiting for server step)
    if (
      currentRound !== undefined &&
      currentRound !== null &&
      currentRound !== lastAcknowledgedRound &&
      (introStatus === "waiting" || introStatus === "completed")
    ) {
      setIsAnnouncing(true);
      setLastAcknowledgedRound(currentRound);
    }
  }, [currentRound, lastAcknowledgedRound, introStatus]);

  // Effect to detect new picks/bans
  useEffect(() => {
    const yourPicks = duelState.your_picks || [];
    const opponentPicks = duelState.opponent_picks || [];
    const yourBans = duelState.your_banned_picks || [];
    const opponentBans = duelState.opponent_banned_picks || [];

    const newYourPicks = yourPicks.filter(
      (id) => !prevYourPicks.current.includes(id),
    );
    const newOpponentPicks = opponentPicks.filter(
      (id) => !prevOpponentPicks.current.includes(id),
    );
    const newYourBans = yourBans.filter(
      (id) => !prevYourBans.current.includes(id),
    );
    const newOpponentBans = opponentBans.filter(
      (id) => !prevOpponentBans.current.includes(id),
    );

    // Update refs
    prevYourPicks.current = yourPicks;
    prevOpponentPicks.current = opponentPicks;
    prevYourBans.current = yourBans;
    prevOpponentBans.current = opponentBans;

    // Skip showing step results on initial mount (reconnection scenario)
    // This prevents existing picks/bans from triggering the overlay
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const actions: StepAction[] = [];

    if (newYourPicks.length > 0) {
      actions.push({ type: "pick", actor: "you", pokemonIds: newYourPicks });
    }
    if (newOpponentPicks.length > 0) {
      actions.push({
        type: "pick",
        actor: "opponent",
        pokemonIds: newOpponentPicks,
      });
    }
    if (newYourBans.length > 0) {
      actions.push({ type: "ban", actor: "you", pokemonIds: newYourBans });
    }
    if (newOpponentBans.length > 0) {
      actions.push({
        type: "ban",
        actor: "opponent",
        pokemonIds: newOpponentBans,
      });
    }

    if (actions.length > 0) {
      setActiveStepResult({ actions });
      setIsStepResultExiting(false);

      const displayDuration = 3000;
      const exitDuration = 700; // Matches CSS animation

      // Start exit animation
      const exitTimer = setTimeout(() => {
        setIsStepResultExiting(true);
      }, displayDuration);

      // Remove component
      const cleanupTimer = setTimeout(() => {
        setActiveStepResult(null);
        setIsStepResultExiting(false);
      }, displayDuration + exitDuration);

      return () => {
        clearTimeout(exitTimer);
        clearTimeout(cleanupTimer);
      };
    }
  }, [
    duelState.your_picks,
    duelState.opponent_picks,
    duelState.your_banned_picks,
    duelState.opponent_banned_picks,
  ]);

  // Handle announcement completion
  const handleAnnouncementComplete = useCallback(() => {
    setIsAnnouncing(false);
    console.log("Announcement complete");
  }, []);

  // Helper to check pokemon availability
  const getPokemonAvailability = (pokemonId: number) => {
    // Check bans first - if banned by anyone, it's unavailable
    if (
      duelState.your_banned_picks.includes(pokemonId) ||
      duelState.opponent_banned_picks.includes(pokemonId)
    ) {
      return { disabled: true, reason: "BANNED" };
    }

    if (stepType === "pick") {
      if (duelState.your_picks.includes(pokemonId))
        return { disabled: true, reason: "ALREADY PICKED" };
      if (duelState.opponent_picks.includes(pokemonId))
        return { disabled: true, reason: "TAKEN" };
    }

    if (stepType === "ban") {
      // Cannot ban a pokemon that the opponent has already secured (picked)
      if (duelState.opponent_picks.includes(pokemonId))
        return { disabled: true, reason: "ALREADY SECURED" };
      // Cannot ban your own picks
      if (duelState.your_picks.includes(pokemonId))
        return { disabled: true, reason: "YOUR PICK" };
    }

    return { disabled: false, reason: null };
  };

  const timerData =
    duelState.status === "in_step"
      ? {
          timeRemaining: duelState.current_step?.time_remaining,
          bonusTimeRemaining: duelState.current_step?.bonus_time_remaining ?? 0,
          stepType: stepType,
        }
      : null;

  // Determine Base Content
  let baseContent: React.ReactNode = null;

  // We change priority here: Battle Starting is paramount.
  if (duelState.status === "battle_starting") {
    // Battle starting replaces everything
    return (
      <div className="relative flex justify-center items-center w-full overflow-visible animate-fade-in">
        <BattleStartingDisplay />
      </div>
    );
  }

  // Calculate base content for other states
  if (inspectedPokemon) {
    const { disabled, reason } = getPokemonAvailability(inspectedPokemon.id);

    let showActionButtons = introStatus === "completed";

    // If inspecting enemy pokemon during pick phase, hide action buttons
    if (
      stepType === "pick" &&
      inspectedPokemon.team &&
      `player_${inspectedPokemon.team.toLowerCase()}` !== duelState.player_role
    ) {
      showActionButtons = false;
    }

    // If inspecting your own pokemon during ban phase, hide action buttons
    if (
      stepType === "ban" &&
      inspectedPokemon.team &&
      `player_${inspectedPokemon.team.toLowerCase()}` === duelState.player_role
    ) {
      showActionButtons = false;
    }

    baseContent = (
      <RoundStepBox
        pokemon={inspectedPokemon}
        team={inspectedPokemon.team}
        onConfirm={() => {
          handleSubmitSelection();
          onCancelInspect?.();
        }}
        onCancel={onCancelInspect}
        isConfirmDisabled={selectedPokemonIds.length !== picksCount || disabled}
        stepType={stepType}
        showActionButtons={showActionButtons}
        timerData={timerData}
        disabledReason={reason}
      />
    );
  } else if (introStatus === "presentation" || introStatus === "idle") {
    // Check if team names are available
    const hasTeamNames = teamNames.blueTeamName && teamNames.redTeamName;

    if (!hasTeamNames) {
      baseContent = <DuelLoading />;
    } else {
      baseContent = (
        <TeamNames
          blueTeamName={teamNames.blueTeamName}
          redTeamName={teamNames.redTeamName}
          blueTeamCountry={teamNames.blueTeamCountry ?? null}
          redTeamCountry={teamNames.redTeamCountry ?? null}
          isAnnouncing={isAnnouncing}
          currentRound={currentRound}
          onAnnouncementComplete={handleAnnouncementComplete}
        />
      );
    }
  } else if (isAutoSelecting) {
    baseContent = <AutoSelectionLoader stepType={stepType ?? ""} />;
  } else if (duelState.status === "in_step") {
    baseContent = (
      <RoundStepBox
        pokemon={null}
        team={undefined}
        stepType={stepType}
        timerData={timerData}
        showActionButtons={false}
      />
    );
  } else if (duelState.status === "waiting_for_opponent_pick") {
    baseContent = shouldShowWaiting ? <WaitingForOpponent /> : null;
  } else {
    baseContent = (
      <TeamNames
        blueTeamName={teamNames.blueTeamName}
        redTeamName={teamNames.redTeamName}
        blueTeamCountry={teamNames.blueTeamCountry ?? null}
        redTeamCountry={teamNames.redTeamCountry ?? null}
      />
    );
  }

  return (
    <div className="relative flex justify-center items-center w-full overflow-visible transition-all duration-300 ease-in-out">
      {/* Base Content Layer */}
      <div
        className={classNames(
          "transition-opacity duration-300 ease-in-out w-full flex justify-center",
          activeStepResult ? "opacity-0" : "opacity-100",
        )}
      >
        <div className="flex justify-center w-full animate-fade-in">
          {baseContent}
        </div>
      </div>

      {/* Step Result Overlay */}
      {activeStepResult && (
        <StepResultDisplay
          result={activeStepResult}
          allPokemons={
            [
              ...duelState.your_team,
              ...duelState.opponent_team,
            ] as unknown as Pokemon[]
          }
          isExiting={isStepResultExiting}
        />
      )}
    </div>
  );
};

const StepResultDisplay = ({
  result,
  allPokemons,
  isExiting = false,
}: {
  result: StepResultState;
  allPokemons: Pokemon[];
  isExiting?: boolean;
}) => {
  // We can have 1 or 2 actions.
  // If 2 actions, typically "you picked" and "opponent picked" simultaneously.
  // We want to show them side-by-side or stacked, sliding in from edges.

  // Separate actions by actor
  const yourAction = result.actions.find((a) => a.actor === "you");
  const opponentAction = result.actions.find((a) => a.actor === "opponent");

  return (
    <div className="z-50 fixed inset-0 flex flex-col justify-center items-center pointer-events-none">
      {/* Your Action Panel - Slides from Left */}
      {yourAction && (
        <div
          className={classNames(
            "absolute left-0 top-1/2",
            isExiting ? "animate-slide-out-left" : "animate-slide-in-left",
          )}
        >
          <ActionPanel
            action={yourAction}
            allPokemons={allPokemons}
            align="left"
          />
        </div>
      )}

      {/* Opponent Action Panel - Slides from Right */}
      {opponentAction && (
        <div
          className={classNames(
            "absolute right-0 top-1/2 translate-y-[50%]",
            isExiting ? "animate-slide-out-right" : "animate-slide-in-right",
          )}
        >
          <ActionPanel
            action={opponentAction}
            allPokemons={allPokemons}
            align="right"
          />
        </div>
      )}
    </div>
  );
};

const ActionPanel = ({
  action,
  allPokemons,
  align,
}: {
  action: StepAction;
  allPokemons: Pokemon[];
  align: "left" | "right";
}) => {
  const isBan = action.type === "ban";
  const isYou = action.actor === "you";

  // Colors
  // You: Blue
  // Opponent: Red
  const bgColor = isYou ? "bg-pk-blue-4" : "bg-pk-red-4";
  const titleText = isBan ? "BANNED!" : "PICKED!";

  const scale = isXsScreen() ? 3 : 4;

  return (
    <div
      className={classNames(
        `relative ${bgColor} flex flex-col justify-center items-center gap-2 p-4 shadow-xl z-50`,
        align === "left" ? "rounded-r-2xl " : "rounded-l-2xl ",
      )}
    >
      <StrokedText
        strokeWidth={isXsScreen() ? 4 : 7}
        fontSize={isXsScreen() ? 14 : 24}
        fillColor="#232020"
        strokeColor="#ffffff"
        className="z-20 font-vck uppercase tracking-tight translate-y-[35px]"
      >
        {titleText}
      </StrokedText>

      <div className="top-1/2 left-1/2 z-10 absolute flex gap-4 -translate-x-1/2 -translate-y-1/2">
        {action.pokemonIds.map((id) => {
          const pokemon = allPokemons.find((p) => p.id === id);
          if (!pokemon) return null;

          return (
            <div
              key={id}
              className="relative flex justify-center items-center drop-shadow-2xl"
            >
              <PokemonSprite
                pokemon={pokemon}
                animation={isBan ? "Sleep" : "Idle"}
                className={classNames(
                  " z-10 filter drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]",
                )}
                style={{
                  transform: `scale(${isYou ? "" : "-"}${scale}, ${scale})`,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BattleStartingDisplay = () => {
  return (
    <div className="flex flex-col justify-center items-center gap-6 bg-pk-gray-1/90 shadow-cartoon p-8 border-4 border-pk-gray-3 rounded-sm w-full max-w-md animate-pulse">
      <div className="flex flex-col items-center gap-4">
        <StrokedText
          strokeWidth={8}
          fontSize={isXsScreen() ? 20 : 32}
          fillColor="#fbbf24"
          strokeColor="#232020"
        >
          BATTLE STARTING
        </StrokedText>

        <StrokedText
          strokeWidth={4}
          fontSize={16}
          fillColor="#f4eae7"
          strokeColor="#232020"
        >
          Preparing Arena...
        </StrokedText>

        {/* Pokeball Spinner (Simple CSS) */}
        <div className="relative w-12 h-12 animate-spin">
          <div className="absolute inset-0 bg-white border-4 border-black rounded-full overflow-hidden">
            <div className="top-0 right-0 left-0 absolute bg-red-500 border-black border-b-4 h-1/2" />
            <div className="top-1/2 left-1/2 z-10 absolute bg-white border-4 border-black rounded-full w-4 h-4 -translate-x-1/2 -translate-y-1/2" />
          </div>
        </div>
      </div>
    </div>
  );
};

const AutoSelectionLoader = ({ stepType }: { stepType: string }) => {
  const isBan = stepType === "ban";

  return (
    <div className="relative flex flex-col items-center mx-2 w-full animate-fade-in">
      {/* Main HUD Container - Retro Box Style */}
      <div className="bg-pk-gray-1/95 shadow-cartoon p-1 border-4 border-pk-gray-3 rounded-sm w-full overflow-visible">
        {/* Header / Phase Title */}
        <div
          className={`relative -mt-6 mx-auto w-3/4 h-10 flex items-center justify-center border-4 shadow-cartoon-sm ${
            isBan
              ? "bg-pk-red-3 border-pk-red-4"
              : "bg-pk-blue-3 border-pk-blue-4"
          }`}
        >
          <StrokedText
            strokeWidth={8}
            fontSize={isXsScreen() ? 12 : 20}
            fillColor="#f4eae7"
            strokeColor="#232020"
            className="font-vck uppercase tracking-widest"
          >
            {isBan ? "Ban Phase" : "Pick Phase"}
          </StrokedText>
        </div>

        {/* Content Area */}
        <div className="flex flex-col items-center gap-2 p-2">
          {/* Time Up Message */}
          <div className="flex flex-col items-center gap-2">
            <StrokedText
              strokeWidth={10}
              fontSize={24}
              fillColor="#ff6b6b"
              strokeColor="#232020"
              className="text-center"
            >
              TIME IS UP!
            </StrokedText>
            <StrokedText
              strokeWidth={6}
              fontSize={16}
              fillColor="#8ba4b8"
              strokeColor="#232020"
              className="text-center"
            >
              Selecting random choice...
            </StrokedText>
          </div>

          {/* Loading Animation */}
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
              <div
                className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-4 h-4 animate-bounce"
                style={{ animationDelay: "0s" }}
              />
              <div
                className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-4 h-4 animate-bounce"
                style={{ animationDelay: "0.2s" }}
              />
              <div
                className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-4 h-4 animate-bounce"
                style={{ animationDelay: "0.4s" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WaitingForOpponent = () => {
  return (
    <div className="flex flex-col justify-center items-center gap-6 bg-pk-gray-1/90 shadow-cartoon mx-2 p-4 border-4 border-pk-gray-3 rounded-sm w-auto max-w-md animate-fade-in">
      <div className="flex flex-col items-center gap-4">
        <StrokedText
          strokeWidth={8}
          fontSize={isXsScreen() ? 12 : 24}
          fillColor="#f4eae7"
          strokeColor="#232020"
        >
          Opponent is deliberating...
        </StrokedText>

        {/* Retro Loading Dots */}
        <div className="flex items-center gap-3">
          <div
            className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-3 h-3 animate-bounce"
            style={{ animationDelay: "0s" }}
          />
          <div
            className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-3 h-3 animate-bounce"
            style={{ animationDelay: "0.2s" }}
          />
          <div
            className="bg-pk-yellow-2 shadow-cartoon-sm border-2 border-black w-3 h-3 animate-bounce"
            style={{ animationDelay: "0.4s" }}
          />
        </div>
      </div>
    </div>
  );
};

const DuelLoading = () => {
  return (
    <div className="flex flex-col justify-center items-center gap-6 bg-pk-gray-1/90 shadow-cartoon mx-2 p-8 border-4 border-pk-gray-3 rounded-sm w-full max-w-md animate-fade-in">
      <div className="flex flex-col items-center gap-4">
        <StrokedText
          strokeWidth={8}
          fontSize={isXsScreen() ? 16 : 24}
          fillColor="#f4eae7"
          strokeColor="#232020"
        >
          LOADING DUEL
        </StrokedText>

        {/* Animated Spinner */}
        <div className="border-4 border-pk-yellow-2 border-t-transparent rounded-full w-8 h-8 animate-spin"></div>
      </div>
    </div>
  );
};

const RoundTimer = ({
  timeRemaining,
  stepType,
  bonusTimeRemaining,
  className,
  showHelperText = true,
}: {
  timeRemaining: number | undefined;
  stepType?: string | undefined;
  bonusTimeRemaining?: number;
  className?: string;
  showHelperText?: boolean;
}) => {
  const isUsingBonusTime = timeRemaining === 0 && (bonusTimeRemaining ?? 0) > 0;
  const displayTime = isUsingBonusTime
    ? bonusTimeRemaining
    : timeRemaining ?? 0;

  const maxTime = isUsingBonusTime ? 60 : 10; // TODO: improve this, since its using fixed/magical values (current 10s per step, current 60s starting extra time pool)
  const progress = Math.min(
    100,
    Math.max(0, ((displayTime || 0) / maxTime) * 100),
  );
  const isUrgent = (displayTime || 0) < 10;
  const isBan = stepType === "ban";

  const containerClass = className ?? "w-auto ";

  return (
    <div
      className={classNames(
        "relative flex flex-col items-center animate-fade-in",
        containerClass,
      )}
    >
      {/* Main HUD Container - Retro Box Style */}
      <div className="bg-pk-gray-3/95 shadow-cartoon p-1 border-4 border-pk-gray-3 rounded-sm w-full overflow-visible">
        {/* Header / Phase Title */}
        <div
          className={classNames(
            "relative -mt-6 mx-auto w-3/4 h-8 flex items-center justify-center border-4 shadow-cartoon-sm",
            isBan
              ? "bg-pk-red-3 border-pk-red-4"
              : "bg-pk-blue-3 border-pk-blue-4",
          )}
        >
          <StrokedText
            strokeWidth={isXsScreen() ? 8 : 11}
            fontSize={isXsScreen() ? 18 : 24}
            fillColor="#f4eae7"
            strokeColor="#232020"
            className="font-vck uppercase tracking-widest"
          >
            {isBan ? "Ban Phase" : "Pick Phase"}
          </StrokedText>
        </div>

        {/* Content Area */}
        <div className="flex flex-col gap-2 mt-2">
          {/* Timer Section */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center gap-8 px-1">
              {/* Progress Bar - Retro Style */}
              <div className="relative bg-pk-gray-2 shadow-inner border-4 border-pk-gray-1 h-6 grow">
                <div
                  className={classNames(
                    "absolute top-0 left-0 h-full transition-all duration-200 ease-linear border-r-4 border-white/50",
                    isUsingBonusTime
                      ? "bg-pk-red-3"
                      : isUrgent
                      ? "bg-pk-orange-3"
                      : "bg-pk-blue-3",
                  )}
                  style={{ width: `${progress}%` }}
                />
                {/* Scanline on bar */}
                <div className="absolute inset-0 bg-[length:100%_4px] bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.2)_50%)] pointer-events-none" />
              </div>
              <div className="flex items-end gap-2">
                <span
                  className={classNames(
                    "text-xl font-bold font-vck tabular-nums",
                    isUrgent || isUsingBonusTime
                      ? "text-pk-red-4 animate-pulse"
                      : "text-pk-yellow-2",
                  )}
                >
                  {displayTime ? displayTime.toFixed(1) : "0.0"}s
                </span>
                {(bonusTimeRemaining ?? 0) > 0 && (
                  <div className="flex flex-col items-end">
                    <span className="font-vck text-[8px] text-pk-gray-4 uppercase leading-none tracking-wider">
                      extra
                    </span>
                    <span className="font-vck font-bold tabular-nums text-pk-blue-3 text-xs">
                      {(bonusTimeRemaining ?? 0).toFixed(1)}s
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Explainer Text */}
          {showHelperText && (
            <div className="flex gap-2 mx-auto font-vck text-pk-gray-4 text-xs xs:text-sm text-center tracking-tight">
              {isBan ? "Remove a Poke from" : "Select a Poke from"}

              <p
                className={classNames(
                  "font-bold",
                  isBan ? "text-pk-red-3" : "text-pk-blue-3",
                )}
              >
                {isBan ? "opponent" : "your"} team
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to convert hex color to rgba string
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Type color mapping (hex values from CSS)
const typeColors: Record<string, string> = {
  normal: "#a8a878",
  fire: "#f08030",
  water: "#6890f0",
  electric: "#f8d030",
  grass: "#78c850",
  ice: "#98d8d8",
  fighting: "#c03028",
  poison: "#a040a0",
  ground: "#e0c068",
  flying: "#a890f0",
  psychic: "#f85888",
  bug: "#a8b820",
  rock: "#b8a038",
  ghost: "#705898",
  dragon: "#7038f8",
  dark: "#705848",
  steel: "#b8b8d0",
  fairy: "#ee99ac",
};

const RoundStepBox = ({
  pokemon,
  team,
  onConfirm,
  onCancel,
  isConfirmDisabled,
  stepType,
  showActionButtons = true,
  timerData,
  disabledReason,
}: {
  pokemon?: DuelPokemon | null;
  team?: "A" | "B" | undefined;
  onConfirm?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  isConfirmDisabled?: boolean | undefined;
  stepType?: string | undefined;
  showActionButtons?: boolean;
  timerData?: {
    timeRemaining: number | undefined;
    bonusTimeRemaining: number;
    stepType?: string | undefined;
  } | null;
  disabledReason?: string | null;
}) => {
  const pokemonTypes = useMemo(() => {
    if (!pokemon) return [];
    const pokeName = pokemon.base_pokemon_name as PokeNames;
    const pokeBaseData = POKE_DATA[pokeName];
    return pokeBaseData?.types.filter((type) => type !== undefined) || [];
  }, [pokemon]);

  const purity = useMemo(() => {
    if (!pokemon || !pokemonTypes.length) return 0;
    return calculatePurity(pokemon.talents.map(String), pokemonTypes);
  }, [pokemon, pokemonTypes]);

  const activeSkills = useMemo(() => {
    if (!pokemon) return [];
    return pokemon.active_skills
      .map((skillId) => POKEMON_SKILLS[skillId])
      .filter(Boolean);
  }, [pokemon]);

  const isBan = stepType === "ban";
  const bgClass =
    pokemon && team
      ? team === "A"
        ? "bg-pk-blue-3"
        : "bg-pk-red-3"
      : "bg-pk-gray-1/90";

  return (
    <div
      className={classNames(
        "w-[90%] relative opacity-95 max-w-lg border-4 border-pk-gray-3 shadow-cartoon rounded-sm flex flex-col gap-2 p-1 animate-fade-in transition-all duration-300",
        bgClass,
        isXsScreen()
          ? "max-h-[170px] translate-y-4"
          : "max-h-[220px] translate-y-10",
      )}
    >
      {onCancel && (
        <div
          className="-top-4 -right-4 z-50 absolute flex justify-center items-center bg-pk-red-3 border-4 border-pk-red-4 w-10 h-10 hover:scale-105 active:scale-95 transition-transform cursor-pointer"
          onClick={onCancel}
        >
          <StrokedText
            className="translate-x-0.5 translate-y-0.5"
            fontSize={24}
            strokeWidth={6}
            fillColor="#f4eae7"
          >
            X
          </StrokedText>
        </div>
      )}

      {timerData && (
        <div className="w-full">
          <RoundTimer
            timeRemaining={timerData.timeRemaining}
            bonusTimeRemaining={timerData.bonusTimeRemaining}
            stepType={timerData.stepType}
            className="mx-0 w-full"
            showHelperText={!pokemon}
          />
        </div>
      )}

      {pokemon ? (
        <div className="gap-2 grid grid-cols-5 p-1 h-full">
          {/* Left Column: Name, Types, Sprite & Purity */}
          <div className="relative flex flex-col justify-start gap-2 col-span-2 h-full">
            <StrokedText
              strokeWidth={6}
              fontSize={isXsScreen() ? 14 : 20}
              fillColor="#f4eae7"
              strokeColor="#232020"
              className="truncate uppercase"
            >
              {pokemon.base_pokemon_name}
            </StrokedText>

            <div className="flex flex-row justify-between gap-1">
              {/* Talent Power Bar */}
              <div className="z-20 flex flex-col gap-0 w-[67%]">
                <StrokedText
                  strokeWidth={4}
                  fontSize={10}
                  fillColor="#f4eae7"
                  strokeColor="#232020"
                  className="text-center"
                >
                  TALENT POWER
                </StrokedText>
                <SegmentedProgressBar
                  ratio={purity / 9}
                  variant={
                    purity > 6
                      ? "purple"
                      : purity > 4
                      ? "green"
                      : purity > 2
                      ? "orange"
                      : "red"
                  }
                  maxSquares={9}
                  segmented={false}
                />
              </div>

              <div className="flex flex-col gap-1">
                {pokemonTypes.map((type) => {
                  const typeName = type.toLowerCase();
                  const typeColor = typeColors[typeName];
                  const backgroundColor = typeColor
                    ? hexToRgba(typeColor, 1)
                    : undefined;

                  return (
                    <div
                      key={type}
                      className={`flex items-center justify-center rounded-md p-1 w-6 h-6`}
                      style={{ backgroundColor }}
                      title={type}
                    >
                      <img
                        src={`/assets/pokemon-types/${typeName}.svg`}
                        alt={type}
                        className="w-4 h-4"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="top-2 left-2 absolute flex justify-center items-center mt-auto mb-4 rounded-sm h-8 xs:h-14 aspect-square overflow-visible b">
              <div className="absolute inset-0 bg-[length:8px_8px] bg-[radial-gradient(#000_2px,transparent_2px)] opacity-10" />
              <PokemonSprite
                pokemon={pokemon}
                className="z-40 -translate-y-12 xs:-translate-y-4"
                style={{ scale: 2 }}
                showShadow={true}
              />
            </div>
          </div>

          {/* Right Column: Stats & Skills */}
          <div className="flex flex-col gap-3 col-span-3 h-full">
            {/* Active Skills */}
            <div className="flex flex-col gap-1">
              <div
                className={classNames(
                  "grid grid-cols-3 gap-2",
                  isXsScreen() ? "h-auto" : "h-[90px]",
                )}
              >
                {activeSkills.map((skill) =>
                  skill ? (
                    <div key={skill.id} className="w-full h-full">
                      <MiniSkillCard skill={skill} />
                    </div>
                  ) : null,
                )}

                {/* Fillers */}
                {Array.from({
                  length: Math.max(0, 3 - activeSkills.length),
                }).map((_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="bg-pk-gray-2/10 border-2 border-pk-gray-3 border-dashed rounded-lg"
                  />
                ))}
              </div>
            </div>

            {/*Buttons */}
            {showActionButtons &&
              onConfirm &&
              onCancel &&
              (disabledReason ? (
                <div className="font-vck text-pk-gray-4 text-xs xs:text-sm tracking-tight">
                  {disabledReason}
                </div>
              ) : (
                <div className="flex gap-2 pt-0">
                  <Button
                    variant={isBan ? "red" : "blue"}
                    onClick={onConfirm}
                    disabled={
                      isConfirmDisabled ||
                      !!disabledReason ||
                      disabledReason !== null
                    }
                    className={classNames(
                      "flex-[2] border-4 shadow-cartoon active:translate-y-1 active:shadow-none rounded-none",
                      "disabled:opacity-50 disabled:cursor-not-allowed disabled:grayscale",
                    )}
                  >
                    <StrokedText
                      strokeWidth={6}
                      fontSize={isXsScreen() ? 13 : 18}
                      fillColor="#ffffff"
                      strokeColor="#232020"
                    >
                      {disabledReason || (isBan ? "CONFIRM BAN" : "LOCK IN")}
                    </StrokedText>
                  </Button>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 justify-center items-center gap-4 opacity-50">
          <div className="font-vck text-pk-gray-3 text-sm">
            Choose a Pokemon from the list
          </div>
        </div>
      )}
    </div>
  );
};

const TeamNames = ({
  blueTeamName,
  redTeamName = "",
  blueTeamCountry,
  redTeamCountry,
  isAnnouncing = false,
  currentRound,
  onAnnouncementComplete,
}: {
  blueTeamName: string;
  redTeamName?: string;
  blueTeamCountry?: string | null;
  redTeamCountry?: string | null;
  isAnnouncing?: boolean;
  currentRound?: number;
  onAnnouncementComplete?: () => void;
}) => {
  return (
    <div className="flex flex-col justify-center items-center gap-0 w-full animate-fade-in">
      {redTeamName && (
        <TeamBanner
          name={redTeamName}
          country={redTeamCountry ?? null}
          color="red"
        />
      )}

      {/* Center section - VS or Round Announcement */}
      <div className="relative">
        {isAnnouncing && currentRound && (
          <RoundAnnouncement
            round={currentRound}
            onComplete={onAnnouncementComplete ?? (() => {})}
            className="z-50 absolute inset-0"
          />
        )}

        {/* VS Text - only show when not announcing */}
        <div className="flex justify-center items-center">
          <StrokedText
            strokeWidth={16}
            fontSize={isXsScreen() ? 24 : 48}
            fillColor="#fbbf24"
            strokeColor="#000"
            className="drop-shadow-[0_0_30px_rgba(251,191,36,0.6)] italic"
          >
            VS
          </StrokedText>
        </div>
      </div>

      <TeamBanner
        name={blueTeamName}
        country={blueTeamCountry ?? null}
        color="blue"
      />
    </div>
  );
};

const TeamBanner = ({
  name,
  country,
  color,
}: {
  name: string;
  country?: string | null;
  color: "red" | "blue";
}) => {
  const isRed = color === "red";
  const bgColor = isRed
    ? "bg-linear-30 from-pk-red-3 to-red-500"
    : "bg-linear-30 from-pk-blue-3 to-blue-500";
  const borderColor = isRed ? "border-pk-red-4" : "border-pk-blue-4";

  return (
    <div className="relative flex justify-center items-center shadow-cartoon w-64 h-12">
      {/* Banner Background */}
      <div className={`absolute inset-0 ${bgColor} border-4 ${borderColor}`} />

      {/* Pixel Corners */}
      <div className="top-0 left-0 absolute bg-white/20 rounded-full w-2 h-2" />
      <div className="top-0 right-0 absolute bg-white/20 rounded-full w-2 h-2" />
      <div className="bottom-0 left-0 absolute bg-white/20 rounded-full w-2 h-2" />
      <div className="right-0 bottom-0 absolute bg-white/20 rounded-full w-2 h-2" />

      <div className="z-10 relative flex items-center gap-3 px-4">
        {country && (
          <div className="flex flex-shrink-0 justify-center items-center drop-shadow-md">
            <CountryFlag countryCode={country} size="md" />
          </div>
        )}
        <StrokedText
          strokeWidth={8}
          fontSize={24}
          fillColor="#f4eae7"
          strokeColor="#232020"
          className="max-w-[180px] font-vck truncate"
        >
          {name}
        </StrokedText>
      </div>
    </div>
  );
};
